// eslint-disable-next-line no-unused-vars
import React, { memo } from 'react';
import { bool, string, func, node, array, oneOfType, number } from 'prop-types';
import {
  DataTableSkeleton, TableHeader, TableRow, Table, TableHead, TableBody, Button, Loading
} from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { isNumber } from 'lodash';

import { formatNumber } from 'Utils/formatters';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import { NWOrderTypeBadge } from 'Components/shared';

const StyledContainer = styled.div`
  width: 100%;
  min-width: 100%;

  .supplierHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    color: ${ props => props.theme.colors.darkGray };

    .supplierName {
      font-size: 24px;
      display: flex;
      gap: 20px;
      align-items: center;

      span {
        font-weight: 600;
      }
    }
  }

  .supplierFooter {
    display: flex;
    justify-content: flex-end;
    padding: 15px 0;
    margin-bottom: 20px;
    color: #999;
    font-size: 20px;

    span {
      text-transform: capitalize;
      font-weight: 600;
    }
  }

  .back {
    display: block;
    padding: 0;
    width: 200px;
    height: 40px;
    border: 1px solid ${ props => props.theme.colors.blue };
    text-align: center;
    font-size: 0.875rem;
    line-height: 40px;
    color: ${ props => props.theme.colors.blue };
  }

  .actions {
    display: flex;
    justify-content: space-between;
    padding: 15px;

    > div {
      display: flex;
      gap: 15px;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    border: none;

    > div {
      overflow: visible
    }

    .supplierHeader {
      flex-direction: column;
      align-items: start;
      gap: 10px;
    }

    .actions {
      flex-direction: column;
      gap: 15px;
      padding: 15px 0;

      > button {
        min-width: 100%;
      }

      > div {
        flex-direction: column;

        > button {
          min-width: 100%;
        }
      }
    }
  }
`;

export const StyledButton = styled(Button)`
  height: 40px;
  padding: 0;
  border: 1px solid ${ props => props.theme.colors.blue };
  color: ${ props => props.theme.colors.blue };
  justify-content: center;

  &.cds--btn {
    padding: 9px 24px;
  }

  &.saveChanges {
    border: none;
    color: #fff;
    background-color: #1F59A2;
    
    &:disabled {
      padding-left: 32px;
    }
  }

  &.cancelAllChanges {
    color: #D52B1E;
    border: 1px solid #D52B1E;
    
    &:disabled {
      padding-left: 32px;
    }
  }
`;

export const StyledProductsTable = styled(Table)`
  table-layout: fixed;
  border: 0 none;
`;

export const StyledTableHead = styled(TableHead)`
  background-color: ${ props => props.theme.products.addToFavorite };
  font-weight: 600;
  font-size: 0.875rem;
  color: ${ props => props.theme.colors.darkGray };
  text-align: center;

  th {
    background-color: ${ props => props.theme.products.colors.addToFavorite };
    > div {
      text-align: center !important;
    }

    &.name {
      width: ${ props => props.theme.ds ? '35%' : '40%' };
      > div {
        text-align: left !important;
      }
    }

    &.quantity {
      width: 15%;
    }

    &.unitPriceNoVat {
      width: ${ props => props.theme.ds ? '15%' : '20%' }
    }

    &.totalPriceNoVat {
      /* width: calc(15% - 55px); */
    }

    &.totalPriceWithVat {
      width: 15%;
    }

    &.delete {
      width: 55px;
      padding: 0;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    display: none;
  }
`;

export const StyledTableBody = styled(TableBody)`
  background-color: transparent !important;
  padding: 0 15px;

  tr:last-of-type {
    td {
      border-bottom: none !important;
    }
  }

  tr.rowBorder:last-of-type {
    display: none;
  }

  td {
    background-color: transparent;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    tr.rowBorder:last-of-type {
      display: table-row;
    }
  }
`;

export const StyledDataTableSkeleton = styled(DataTableSkeleton)`
  border: 1px solid ${ props => props.theme.products.colors.border };
  thead tr th {
    background-color: ${ props => props.theme.products.colors.addToFavorite };
  }
  tbody tr td {
    background: ${ props => props.theme.colors.white };
    outline: 0;
    border-top: 0;
    border-bottom: 2px solid ${ props => props.theme.products.colors.border };
  }
  tbody tr:last-of-type {
    border-bottom: 0;
  }
`;

const OrderProducts = memo(props => {
  const {
    actionsDisabled,
    loading,
    ds,
    defaultCurrency,
    id,
    editable,
    columns,
    onOpenModal,
    onSaveProductUpdate,
    hasProductsWithChanges,
    loadingOrderUpdate,
    loadingCancelChanges,
    onCancelAllChanges,
    children,
    supplierId,
    supplierIndex,
    supplierTotalValue,
    supplierOfferDeliveryTimeDays,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const showSupplierInfo = isNumber(supplierIndex) && !id;

  if (loading) {
    return (
      <StyledDataTableSkeleton
        theme={ theme }
        showHeader={ false }
        showToolbar={ false }
        headers={ columns }
        columnCount={ columns.length }
        rowCount={ 5 }
      />
    );
  }

  if (!children.length) {
    return (
      <StyledContainer theme={ theme }>
        <h4>{ t('cart.empty') }</h4>
        <Link
          to="/products"
          className="back"
        >
          { '<' } { t('common.backTo', { label: t('products.products') }) }
        </Link>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer theme={ theme }>
      { showSupplierInfo && (
        <div className="supplierHeader">
          <div className="supplierName">
            <span>{ `${ t('orders.order') } ${ supplierIndex + 1 } ` }</span>
            <NWOrderTypeBadge>
              { supplierId !== 'default' ? t('products.supplierStock') : t('products.nodStock') }
            </NWOrderTypeBadge>
          </div>
          <DeliveryEstimate estimate={ supplierOfferDeliveryTimeDays } isExternalSupplier={ supplierId !== 'default' } />
        </div>
      ) }
      <StyledProductsTable theme={ theme }>
        <StyledTableHead theme={{ ...theme, ds }}>
          <TableRow>
            { columns.map(col => (
              <TableHeader key={ col.key } className={ col.key }>
                { col.header }
              </TableHeader>
            )) }
          </TableRow>
        </StyledTableHead>
        <StyledTableBody>
          { children }
        </StyledTableBody>
      </StyledProductsTable>
      { showSupplierInfo && (
        <div className="supplierFooter">
          <div>
            { `${ t('orders.orderSubtotal') }: ` }
            <span>{ formatNumber(supplierTotalValue, { currency: defaultCurrency }) }</span>
          </div>
        </div>
      ) }
      { (editable && id) && (
        <div className="actions">
          <StyledButton
            kind="ghost"
            disabled={ actionsDisabled }
            theme={ theme }
            onClick={ onOpenModal }
          >
            + { t('orders.addProduct') }
          </StyledButton>
          { hasProductsWithChanges && (
            <div>
              <StyledButton
                className="saveChanges"
                kind="ghost"
                disabled={ loadingOrderUpdate }
                theme={ theme }
                onClick={ onSaveProductUpdate }
              >
                { loadingOrderUpdate && <Loading withOverlay={ false } small active /> }
                { t('orders.saveChanges') }
              </StyledButton>
              <StyledButton
                className="cancelAllChanges"
                kind="ghost"
                disabled={ loadingCancelChanges }
                theme={ theme }
                onClick={ onCancelAllChanges }
              >
                { loadingCancelChanges && <Loading withOverlay={ false } small active /> }
                { t('orders.cancelAllChanges') }
              </StyledButton>
            </div>
          ) }
        </div>
      ) }
    </StyledContainer>
  );
});
OrderProducts.displayName = 'OrderProductsComponent';
OrderProducts.propTypes = {
  loading: bool,
  actionsDisabled: bool,
  ds: bool,
  defaultCurrency: string,
  id: string,
  onOpenModal: func,
  editable: bool,
  columns: array,
  onSaveProductUpdate: func,
  hasProductsWithChanges: bool,
  loadingOrderUpdate: bool,
  loadingCancelChanges: bool,
  onCancelAllChanges: func,
  children: node,
  supplierId: oneOfType([string, bool]),
  supplierIndex: oneOfType([string, number]),
  supplierTotalValue: oneOfType([string, number]),
  supplierOfferDeliveryTimeDays: oneOfType([string, number]),
};

export default OrderProducts;
